import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '~/components/UI/shadcn/accordion'

import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import HtmlBlock from '~/components/Helpers/HtmlBlock'
import { useState } from 'react'

const accordionStyles = cva('', {
  variants: {
    colour: {
      'ink-blue': 'bg-blue-d text-white',
      'mid-blue': 'bg-blue-m text-white',
      'light-blue': 'bg-blue-l text-blue-d',
      chartreuse: 'bg-char text-blue-d',
      'light-pink': 'bg-pink text-blue-d',
      'light-aqua': 'bg-green text-blue-d',
      'light-purple': 'bg-purple text-blue-d',
      'light-orange': 'bg-orange text-blue-d',
      default: ''
    },
    border: {
      'ink-blue': 'border-blue-d-40',
      'mid-blue': 'border-blue-m-40',
      white: 'border-white/60',
      default: ''
    },
    fill: {
      'ink-blue': 'fill-blue-d',
      'mid-blue': 'fill-blue-m',
      white: 'fill-white',
      default: ''
    },
    defaultVariant: { colour: 'default', border: 'default', fill: 'default' }
  }
})

const CombinedAccordion = ({
  content,
  type,
  transcript,
  children,
  layoutColour,
  onValueChange,
  value
}: AccCont) => {
  const [current, setCurrent] = useState(content.open ? 'item-0' : '')

  // Allow external control of accordion open/close state (for course pages)
  const accordionValue = value !== undefined ? value : current
  const handleValueChange = (newValue: string) => {
    setCurrent(newValue)
    if (onValueChange) {
      onValueChange(newValue)
    }
  }

  const isCustom = type === 'custom'
  const isMulti =
    content?.contentSingleAccordion &&
    Array.isArray(content?.contentSingleAccordion) &&
    content?.contentSingleAccordion.length > 1

  const itemColour =
    content?.accordionStyle === 'standalone'
      ? content?.backgroundColour || 'default'
      : 'default'

  const borderColour =
    content?.accordionStyle === 'inline' || isMulti
      ? ['ink-blue', 'mid-blue'].includes(layoutColour ?? '')
        ? 'white'
        : ['chartreuse', 'white', 'light-grey'].includes(layoutColour ?? '')
          ? 'mid-blue'
          : 'ink-blue'
      : ''

  const accordionItems = () => {
    if (isCustom) {
      return (
        <AccordionItem
          value='item-0'
          className={cn(
            accordionStyles({
              colour: itemColour,
              border: borderColour || 'default',
              fill: null
            }),
            itemColour !== 'default' ? 'pt-s px-s border-none' : '',
            'space-y-xs-plus relative border-b-1'
          )}
        >
          <AccordionTrigger
            className={cn(
              itemColour !== 'default'
                ? '*:group-hover:bg-white/20'
                : '*:group-hover:bg-grey-l',
              'text-h3-sm sm:text-h3-md lg:text-h3-lg pb-xs-plus cursor-pointer font-bold transition-all duration-300'
            )}
          >
            {Array.isArray(content?.contentSingleAccordion)
              ? content?.contentSingleAccordion[0]?.heading
              : content?.heading}

            <svg
              height='1'
              width='100%'
              className={cn(
                'absolute bottom-0 left-0 z-1 origin-left translate-y-[1px] scale-x-0 transition-transform duration-300 group-hover:scale-x-100 group-focus:scale-x-100',
                accordionValue === 'item-0' ? 'scale-x-100' : ''
              )}
            >
              <rect
                x='0'
                y='0'
                width='100%'
                height='1'
                className={cn(
                  accordionStyles({
                    colour: null,
                    border: null,
                    fill: borderColour || 'default'
                  })
                )}
                strokeLinecap='square'
              />
            </svg>
          </AccordionTrigger>
          <AccordionContent className='pb-s pt-0 text-base'>
            {children || <p>No content available</p>}
          </AccordionContent>
        </AccordionItem>
      )
    }
    if (isMulti) {
      return (
        Array.isArray(content?.contentSingleAccordion) &&
        content?.contentSingleAccordion?.map((a, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className={cn(
              accordionStyles({
                colour: null,
                border: borderColour || 'default',
                fill: null
              }),
              'space-y-m relative border-b-1'
            )}
          >
            <AccordionTrigger
              className={cn(
                ['ink-blue', 'mid-blue'].includes(layoutColour ?? '')
                  ? '*:group-hover:bg-white/20'
                  : '*:group-hover:bg-grey-l',
                'text-h3-sm sm:text-h3-md lg:text-h3-lg pb-xs lg:pb-s group cursor-pointer font-bold transition-all duration-300'
              )}
            >
              {a?.heading}

              <svg
                height='1'
                width='100%'
                className={cn(
                  'absolute bottom-0 left-0 z-1 origin-left translate-y-[1px] scale-x-0 transition-transform duration-300 group-hover:scale-x-100 group-focus:scale-x-100',
                  accordionValue === `item-${i}` ? 'scale-x-100' : ''
                )}
              >
                <rect
                  x='0'
                  y='0'
                  width='100%'
                  height='1'
                  className={cn(
                    accordionStyles({
                      colour: null,
                      border: null,
                      fill: borderColour || 'default'
                    })
                  )}
                  strokeLinecap='square'
                />
              </svg>
            </AccordionTrigger>
            <AccordionContent className='pb-m pt-0 text-base'>
              <HtmlBlock content={a?.copy} />
            </AccordionContent>
          </AccordionItem>
        ))
      )
    }

    const singleItem = Array.isArray(content?.contentSingleAccordion)
      ? content?.contentSingleAccordion[0]
      : content?.contentSingleAccordion || content

    return (
      <AccordionItem
        value='item-0'
        className={cn(
          accordionStyles({
            colour: itemColour,
            border: borderColour || 'default',
            fill: null
          }),
          itemColour !== 'default' ? 'pt-s px-s border-none' : '',
          'space-y-xs-plus relative border-b-1'
        )}
      >
        <AccordionTrigger
          className={cn(
            itemColour !== 'default'
              ? '*:group-hover:bg-white/20'
              : '*:group-hover:bg-grey-l',
            'text-h3-sm sm:text-h3-md lg:text-h3-lg pb-xs-plus cursor-pointer font-bold transition-all duration-300'
          )}
        >
          {singleItem?.heading}

          {content?.accordionStyle === 'inline' && (
            <svg
              height='1'
              width='100%'
              className={cn(
                'absolute bottom-0 left-0 z-1 origin-left translate-y-[1px] scale-x-0 transition-transform duration-300 group-hover:scale-x-100 group-focus:scale-x-100',
                accordionValue === 'item-0' ? 'scale-x-100' : ''
              )}
            >
              <rect
                x='0'
                y='0'
                width='100%'
                height='1'
                className={cn(
                  accordionStyles({
                    colour: null,
                    border: null,
                    fill: borderColour || 'default'
                  })
                )}
                strokeWidth='2'
                strokeLinecap='square'
              />
            </svg>
          )}
        </AccordionTrigger>
        <AccordionContent
          className={cn(
            transcript ? 'max-h-[200px] overflow-y-auto' : '',
            'pb-s pt-0 text-base'
          )}
        >
          <HtmlBlock content={singleItem?.copy} />
        </AccordionContent>
      </AccordionItem>
    )
  }

  return (
    <Accordion
      type='single'
      collapsible
      className='gap-xs space-y-m flex w-full flex-col'
      value={accordionValue}
      onValueChange={handleValueChange}
      {...(content.open && { defaultValue: 'item-0' })}
    >
      {accordionItems()}
    </Accordion>
  )
}

const GeneralAccordion = ({
  content,
  type,
  className,
  transcript,
  children,
  canvas,
  alignment,
  layoutColour,
  onValueChange,
  value
}: AccCont) => {
  return (
    <div className='w-full flex-col px-0 text-left lg:flex-row'>
      <div
        className={cn(
          canvas ? (alignment === 'left' ? 'mx-0' : 'm-auto') : '',
          className,
          'space-y-s max-sm:[&>div]:space-y-xs max-lg:[&>div]:space-y-s w-full max-w-[85ch]'
        )}
      >
        <CombinedAccordion
          content={content}
          type={type}
          transcript={transcript}
          layoutColour={layoutColour}
          onValueChange={onValueChange}
          value={value}
        >
          {children}
        </CombinedAccordion>
      </div>
    </div>
  )
}

export default GeneralAccordion
